import { mapKeys, uniq } from 'es-toolkit';
import React from 'react';
import DuplicateIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography } from '@mui/material';
import { useStrictMode } from 'react-konva';

import { TooltipIconButton } from '@paragon/client-tools';
import { enqueueError, InlineNotification } from '@paragon/notification-tools';

import { deleteBundle, duplicateBundle } from '../../../actions/bundleActions';
import { setInstanceBundlePositions } from '../../../actions/productionComponentInstances';
import {
  getAggregatedTrussStatisticsByBundleGuid,
  getAnalysisSetGuidQuantitiesByBundle,
} from '../../../selectors/bundles';
import { getBundlesForBundleList } from '../../../selectors/getBundlesForBundleList';
import { getDistanceFormatterSelector } from '../../../selectors/getDistanceFormatterSelector';
import { getInstanceInfoByBundleGuid } from '../../../selectors/getInstanceInfoByBundleGuid';
import { getInstancePositionsByBundleGuid } from '../../../selectors/getInstancePositionsByBundleGuid';
import { getProductionComponentInstancesByBundleGuid } from '../../../selectors/getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid } from '../../../selectors/getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid';
import { getWarningsByBundleGuid } from '../../../selectors/getWarningsByBundleGuid';
import { getLoadedTrussesByAnalysisSetGuid } from '../../../selectors/trusses';
import { collapseBundle, expandBundle, setBundleBeingEditedGuid } from '../../../store/batchesAndBundlesTabSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { distancesToString } from '../gridUtilities';
import { BundlePositionBuilder } from './bundle-builder/BundlePositionBuilder';

const bundlePositionBuilderWidth = 570;
const bundlePositionBuilderHeight = 300;

export function BundlesAccordion() {
  const dispatch = useAppDispatch();
  const bundlesForBundleList = useAppSelector(getBundlesForBundleList);
  const bundlesToShow = useAppSelector((state) => state.batchesAndBundlesTab.bundlesToShow);
  const productionComponentInstancesByBundleGuid = useAppSelector(getProductionComponentInstancesByBundleGuid);
  const productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid = useAppSelector(
    getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid,
  );
  const analysisSetGuidQuantitiesByBundle = useAppSelector(getAnalysisSetGuidQuantitiesByBundle);
  const expandedBundles = useAppSelector((state) => state.batchesAndBundlesTab.bundlesExpanded);
  const bundleBeingEditedGuid = useAppSelector((state) => state.batchesAndBundlesTab.bundleBeingEditedGuid);
  const loadedTrussesByAnalysisSetGuid = useAppSelector(getLoadedTrussesByAnalysisSetGuid);
  const aggregatedTrussStatisticsByBundleGuid = useAppSelector(getAggregatedTrussStatisticsByBundleGuid);
  const instanceInfoByBundleGuid = useAppSelector(getInstanceInfoByBundleGuid);
  const instancePositionByBundleGuid = useAppSelector(getInstancePositionsByBundleGuid);
  const warningsByBundleGuid = useAppSelector(getWarningsByBundleGuid);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  useStrictMode(true);

  if (!bundlesForBundleList) return <Typography>Select a production group to view its bundles.</Typography>;
  if (bundlesToShow === 'BundlesWithWarnings' && bundlesForBundleList.length === 0)
    return <Typography>No bundles with warnings</Typography>;

  return (
    <Box sx={{ height: '1px', flexGrow: 1, position: 'relative', overflow: 'auto' }}>
      {bundlesForBundleList.map((bundle) => {
        const productionComponentInstancesForBundle = productionComponentInstancesByBundleGuid[bundle.guid] ?? [];
        const analysisSetGuidsForBundle = uniq(
          productionComponentInstancesForBundle.map((instance) => instance.analysisSetGuid),
        );
        const areAllTrussesLoaded = analysisSetGuidsForBundle.every(
          (analysisSetGuid) => analysisSetGuid in loadedTrussesByAnalysisSetGuid,
        );
        if (!areAllTrussesLoaded) return null;
        const canDuplicateBundle = Object.entries(analysisSetGuidQuantitiesByBundle[bundle.guid]).every(
          ([analysisSetGuid, quantity]) => {
            const productionComponentInstancesForActiveProductionGroupNotBundledForAnalysisSet =
              productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid[analysisSetGuid];
            if (!productionComponentInstancesForActiveProductionGroupNotBundledForAnalysisSet) return false;
            return productionComponentInstancesForActiveProductionGroupNotBundledForAnalysisSet.length >= quantity;
          },
        );
        return (
          <Accordion
            expanded={expandedBundles.includes(bundle.guid)}
            onChange={(event, expanded) =>
              expanded ? dispatch(expandBundle(bundle.guid)) : dispatch(collapseBundle(bundle.guid))
            }
            key={bundle.guid}
            // Set this so that we only render canvases for open Accordions, not for every bundle.
            // See https://mui.com/material-ui/react-accordion/#performance.
            slotProps={{ transition: { unmountOnExit: true } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid container sx={{ width: '100%' }}>
                <Grid size={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  Bundle {bundle.id}
                </Grid>
                <Grid size={5} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'text.secondary' }}>
                    {productionComponentInstancesForBundle.length}
                    {` truss${productionComponentInstancesForBundle.length !== 1 ? 'es' : ''}, Height: `}
                    {distancesToString(aggregatedTrussStatisticsByBundleGuid[bundle.guid].heights, distanceFormatter)}
                  </Typography>
                </Grid>
                <Grid size={2}>
                  <Button
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (bundle.scheduleGroupGuid !== null) {
                        dispatch(enqueueError('You cannot edit bundles that are part of a Schedule Group.'));
                        return;
                      }
                      dispatch(expandBundle(bundle.guid));
                      bundle.guid === bundleBeingEditedGuid
                        ? dispatch(setBundleBeingEditedGuid(null))
                        : dispatch(setBundleBeingEditedGuid(bundle.guid));
                    }}
                  >
                    {bundle.guid === bundleBeingEditedGuid ? 'Done' : 'Edit'}
                  </Button>
                </Grid>
                <Grid size={1}>
                  <TooltipIconButton
                    title={
                      bundle.scheduleGroupGuid !== null
                        ? 'Cannot delete bundle that is scheduled. First return it to the backlog.'
                        : 'Delete bundle'
                    }
                    onClick={() => dispatch(deleteBundle(bundle))}
                    disabled={bundle.guid !== bundleBeingEditedGuid || bundle.scheduleGroupGuid !== null}
                  >
                    <DeleteIcon />
                  </TooltipIconButton>
                </Grid>
                <Grid size={1}>
                  <TooltipIconButton
                    title={
                      canDuplicateBundle
                        ? 'Duplicate bundle'
                        : 'Not enough unbundled components remain to duplicate this bundle'
                    }
                    onClick={() => dispatch(duplicateBundle(bundle))}
                    disabled={!canDuplicateBundle}
                  >
                    <DuplicateIcon />
                  </TooltipIconButton>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              {warningsByBundleGuid[bundle.guid].length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {warningsByBundleGuid[bundle.guid].map((warning) => (
                    <InlineNotification type="warning" key={warning.longMessage}>
                      {warning.longMessage}
                    </InlineNotification>
                  ))}
                </Box>
              )}
              <div style={{ width: bundlePositionBuilderWidth }}>
                <BundlePositionBuilder
                  height={bundlePositionBuilderHeight}
                  width={bundlePositionBuilderWidth}
                  instanceInfo={instanceInfoByBundleGuid[bundle.guid]}
                  instancePositions={instancePositionByBundleGuid[bundle.guid]}
                  setInstancePositions={(updatedPositions) =>
                    void dispatch(setInstanceBundlePositions(updatedPositions))
                  }
                  quantitiesByTrussName={mapKeys(
                    analysisSetGuidQuantitiesByBundle[bundle.guid],
                    (quantity, analysisSetGuid) => loadedTrussesByAnalysisSetGuid[analysisSetGuid]?.name,
                  )}
                  disabled={bundleBeingEditedGuid !== bundle.guid}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
